/**
 * Family Portal Layout
 *
 * Simplified layout for family members with left sidebar navigation
 */

import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Bell, Calendar, ClipboardList, HeartPulse, Home, MessageCircle, Settings, LogOut, X, Menu } from 'lucide-react';
import { useAuth } from '@/core/hooks';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/family-portal', icon: <Home className="h-5 w-5" /> },
  { label: 'Schedule', path: '/family-portal/schedule', icon: <Calendar className="h-5 w-5" /> },
  { label: 'Messages', path: '/family-portal/messages', icon: <MessageCircle className="h-5 w-5" /> },
  { label: 'Care Plan', path: '/family-portal/care-plan', icon: <ClipboardList className="h-5 w-5" /> },
  { label: 'Health Updates', path: '/family-portal/health-updates', icon: <HeartPulse className="h-5 w-5" /> },
  { label: 'Notifications', path: '/family-portal/notifications', icon: <Bell className="h-5 w-5" /> },
];

export const FamilyPortalLayout: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    sessionStorage.removeItem('familyMemberId');
    sessionStorage.removeItem('clientId');
    navigate('/login');
  };

  return (
    <div className="app-shell min-h-screen bg-gray-50 text-gray-900">
      {/* Mobile backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex h-24 items-center justify-between border-b border-purple-100 bg-gradient-to-r from-[#fbf7fd] to-white px-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-white shadow-sm ring-1 ring-[#dec6e8]">
                <img src="/images/fuchsia-logo-mark.png" alt="" className="h-12 w-12 object-contain" />
              </div>
              <div className="min-w-0">
                <img src="/images/fuchsia-logo-full.png" alt="Fuchsia Homecare" className="h-10 w-auto max-w-[170px] object-contain" />
                <p className="mt-1 truncate text-xs font-semibold text-slate-500">Family Portal</p>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden rounded-md p-2 text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/family-portal'}
                onClick={() => setIsSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#6c3a80] text-white shadow-sm'
                      : 'text-gray-700 hover:bg-[#fbf7fd] hover:text-[#5e3474]'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Footer with Settings and Logout */}
          <div className="border-t border-gray-200 p-3 space-y-1">
            <NavLink
              to="/family-portal/settings"
              onClick={() => setIsSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#6c3a80] text-white shadow-sm'
                    : 'text-gray-700 hover:bg-[#fbf7fd] hover:text-[#5e3474]'
                }`
              }
            >
              <Settings className="h-5 w-5" />
              Settings
            </NavLink>
            <button
              onClick={() => {
                handleLogout();
                setIsSidebarOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 w-full transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top header bar for mobile menu button */}
        <header className="border-b border-purple-100 bg-white/90 shadow-sm backdrop-blur-xl lg:hidden">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            <img src="/images/fuchsia-logo-full.png" alt="Fuchsia Homecare" className="h-11 w-auto max-w-[170px] object-contain" />
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Main content */}
        <section className="hidden border-b border-purple-100/80 bg-white/76 px-4 py-3 shadow-sm shadow-slate-950/5 backdrop-blur-xl lg:block">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#6c3a80]">Fuchsia Homecare Gorleston</p>
              <p className="text-sm font-semibold text-slate-700">Family-safe updates, visits, messages and documents.</p>
            </div>
            <p className="text-xs font-semibold text-slate-600">Great Yarmouth, NR31 0LN · Tel: 01493 738488</p>
          </div>
        </section>

        <main className="p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
